
import {
  computed,
  defineComponent,
  onMounted,
  provide,
  reactive,
  ref,
} from 'vue'
import ApiService from '@/core/services/ApiService'
import { ElMessage, ElMessageBox } from 'element-plus'
import InterfaceFilterVideosData from '@/assets/ts/_utils/models/FilterVideosData'
import VueEasyLightbox from 'vue-easy-lightbox'
import isEmptyObject from '@/core/helpers/isEmptyObject'
import VideosFilter from '@/core/components/filters/VideosFilter.vue'

interface InterfaceVideosObject {
  id: number
  name: string
  checked: boolean
  status: string
  photo: string
  has_user: boolean
  tags: Array<string>
  reasons: Array<string>
  rejected_count: number
  created_at: string
  updated_at: string
  lightboxVisible: boolean
}

export default defineComponent({
  name: 'VideosList',
  components: { VideosFilter, VueEasyLightbox },
  setup() {
    const rowClasses =
      'd-flex flex-column align-items-start justify-content-start'
    const reasons = ref([])
    const checkedModel = ref(false)
    const isLoading = ref(false)
    const itemsList = ref<InterfaceVideosObject[]>([])
    const totalPages = ref(0)
    const totalItems = ref(0)
    const lightboxVisible = ref(false)
    const lightboxImage = ref<string | null>(null)

    const openLightbox = (src: string) => {
      lightboxImage.value = src
      lightboxVisible.value = true
    }

    const closeLightbox = () => {
      lightboxVisible.value = false
      lightboxImage.value = null
    }

    const videosFilterData = reactive({
      page: null,
      limit: null,
      'filter[user_id]': null,
      'filter[name]': null,
      'filter[status]': null,
      'filter[type]': null,
      'filter[external_video_id]': null,
      'filter[external_video_url]': null,
    } as InterfaceFilterVideosData)

    provide('videosFilterData', videosFilterData)

    const convertDataForEdit = (
      items: InterfaceVideosObject[]
    ): InterfaceVideosObject[] => {
      return items.map(item => ({
        ...item,
        reasons: [],
        checked: false,
        lightboxVisible: false,
      }))
    }

    const requestItems = () => {
      const params: InterfaceFilterVideosData = { ...videosFilterData }

      checkedModel.value = false
      isLoading.value = true
      itemsList.value = []

      ApiService.query('/admin/videos', { params })
        .then(response => {
          const data = response.data
          videosFilterData.page = data.page
          videosFilterData.limit = data.limit
          totalPages.value = data.pages
          totalItems.value = data.total
          itemsList.value = convertDataForEdit(data.items)
          isLoading.value = false
        })
        .catch(() => {
          ElMessage.warning('Ошибка запроса списка видео')
          isLoading.value = false
        })
    }

    const requestReasons = async () => {
      const reasonsObject = await ApiService.query(
        '/admin/reference/moder-reasons?type=video',
        {}
      )
        .then(response => {
          return response.data
        })
        .catch(() => {
          console.error('Error to get reasons')
        })

      if (reasonsObject && !isEmptyObject(reasonsObject)) {
        for (const [key, value] of Object.entries(reasonsObject)) {
          reasons.value.push({
            key,
            value,
          })
        }
      } else {
        ElMessage.warning('Что-то не так с запросом причин')
      }
    }

    const detectUncheckedReasonsOnCheckedEntries = computed(() => {
      const entries = itemsList.value.map(item => {
        if (item.checked && item.reasons.length) {
          return 1
        } else if (item.checked && !item.reasons.length) {
          return 2
        } else if (!item.checked && item.reasons.length) {
          return 3
        }
      })
      return entries.some(item => item === 2)
    })

    const someCheckboxesAreChecked = computed(() => {
      return itemsList.value.some(item => item.checked === true)
    })

    const bulkRejectStatus = computed(() => {
      return (
        !someCheckboxesAreChecked.value ||
        detectUncheckedReasonsOnCheckedEntries.value
      )
    })

    const rejectButtonTitle = computed(() => {
      let title = ''
      if (
        !someCheckboxesAreChecked.value &&
        !detectUncheckedReasonsOnCheckedEntries.value
      ) {
        title = 'Выберите хотя бы одно видео из списка'
      } else if (
        someCheckboxesAreChecked.value &&
        detectUncheckedReasonsOnCheckedEntries.value
      ) {
        title = 'Выберите причину отклонения для выбранных видео'
      } else {
        title = 'Отклонить видео'
      }
      return title
    })

    const getReasonsOfCheckedEntries = computed(() => {
      const entriesObject = {}
      itemsList.value.forEach(item => {
        if (item.id && item.checked === true && item.reasons.length) {
          console.log('> item.reasons', item.reasons)
          entriesObject[item.id] = item.reasons.map(item => item['value']['id'])
        }
      })
      return entriesObject
    })

    const getCheckedEntriesIds = computed(() => {
      const idsArray = []
      itemsList.value.forEach(item => {
        if (item.id && item.checked === true) {
          idsArray.push(item.id)
        }
      })
      return idsArray
    })

    const requestToAllowBulkEntries = async ids => {
      const params = {
        ids: ids,
      }

      return await ApiService.post('/admin/videos/bulk-approve', params)
        .then(response => {
          ElMessage({
            type: 'success',
            message: 'Одобрение подтверждено',
          })
          return response.data.result
        })
        .catch(() => {
          ElMessage({
            type: 'warning',
            message: 'Что-то не так с массовым одобрением видео',
          })
          return false
        })
    }

    const allowEntriesBulk = () => {
      ElMessageBox.confirm(
        'Вы действительно хотите одобрить выбранные видео?',
        'Внимание',
        {
          type: 'success',
          cancelButtonText: 'Отмена',
          confirmButtonText: 'Одобрить',
          beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
              instance.confirmButtonLoading = true
              instance.confirmButtonText = 'В процессе...'
              const result = await requestToAllowBulkEntries(
                getCheckedEntriesIds.value
              )
              if (result) {
                instance.confirmButtonLoading = false
                requestItems()
                done()
              }
              done()
            } else {
              ElMessage({
                type: 'info',
                message: 'Одобрение отменено',
              })
              done()
            }
          },
        }
      )
    }

    const requestToRejectEntriesBulk = reasonsObject => {
      const params = {
        reasons: reasonsObject,
      }

      return ApiService.post('/admin/videos/bulk-reject', params)
        .then(response => {
          return response.data.result
        })
        .catch(() => {
          ElMessage({
            type: 'warning',
            message: 'Что-то не так с отклонением видео',
          })
        })
    }

    const rejectEntriesBulk = () => {
      ElMessageBox.confirm(
        'Вы действительно хотите отклонить выбранные видео?',
        'Внимание',
        {
          type: 'warning',
          cancelButtonText: 'Отмена',
          confirmButtonText: 'Отклонить',
          beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
              instance.confirmButtonLoading = true
              instance.confirmButtonText = 'В процессе...'
              const result = await requestToRejectEntriesBulk(
                getReasonsOfCheckedEntries.value
              )
              if (result) {
                instance.confirmButtonLoading = false
                requestItems()
                ElMessage({
                  type: 'success',
                  message: 'Отклонение видео подтверждено',
                })
                done()
              }
            } else {
              ElMessage({
                type: 'info',
                message: 'Отклонение видео отменено',
              })
              done()
            }
          },
        }
      )
    }

    const requestToDeleteBulkEntries = ids => {
      const params = {
        ids: ids,
      }

      return ApiService.post('/admin/videos/bulk-destroy', params)
        .then(response => {
          return response.data.result
        })
        .catch(() => {
          ElMessage({
            type: 'warning',
            message: 'Что-то не так с массовым удалением видео',
          })
        })
    }

    const deleteEntriesBulk = () => {
      ElMessageBox.confirm(
        'Вы действительно хотите удалить выбранные видео?',
        'Внимание',
        {
          type: 'warning',
          cancelButtonText: 'Отмена',
          confirmButtonText: 'Удалить',
          beforeClose: (action, instance, done) => {
            if (action === 'confirm') {
              instance.confirmButtonLoading = true
              instance.confirmButtonText = 'В процессе...'
              const result = requestToDeleteBulkEntries(
                getCheckedEntriesIds.value
              )
              if (result) {
                instance.confirmButtonLoading = false
                requestItems()
                done()
              }
              ElMessage({
                type: 'success',
                message: 'Удаление видео подтверждено',
              })
              done()
            } else {
              ElMessage({
                type: 'info',
                message: 'Удаление видео отменено',
              })
              done()
            }
          },
        }
      )
    }

    const makeStatusTitle = status => {
      return status === 'confirmed'
        ? 'Одобренные'
        : status === 'rejected'
        ? 'Отклонённые'
        : status === 'not_verified'
        ? 'Не проверенные'
        : 'Nothing'
    }

    const checkItemOnReasonSelect = index => {
      const clickedItems = itemsList.value[index]
      clickedItems.checked = !!clickedItems.reasons.length
    }

    const syncParamsToUrl = params => {
      const newUrl =
        window.location.protocol +
        '//' +
        window.location.host +
        window.location.pathname +
        '?' +
        params
      // This replaces the current URL with the recreated.
      window.history.pushState({ path: newUrl }, '', newUrl)
    }

    const convertFilterToParams = (data: InterfaceFilterVideosData) => {
      let paramsAsString = ''
      // Iterate the filter object params.
      for (const [key, value] of Object.entries(data)) {
        if (value && Boolean(value)) {
          if (!paramsAsString.length) {
            // The 1st element in the params row.
            paramsAsString += key + '=' + value
          } else {
            // The rest elements in the params row.
            paramsAsString += '&' + key + '=' + value
          }
        }
      }
      // console.log('> convert >> paramsAsString', paramsAsString);
      return paramsAsString
    }

    const filterVideosButtonClick = () => {
      syncParamsToUrl(convertFilterToParams(videosFilterData))
      requestItems()
    }

    const switchAllCheckboxes = () => {
      if (checkedModel.value) {
        itemsList.value.forEach(item => {
          item.checked = false
        })
      } else {
        itemsList.value.forEach(item => {
          item.checked = true
        })
      }
    }

    const tableHasContent = computed(() => {
      return itemsList.value.length > 0 && !isLoading.value
    })

    const requestToAllowEntry = item => {
      return ApiService.post(`/admin/videos/${item.id}/approve`, {
        id: item.id,
      })
        .then(response => {
          return response.data.result
        })
        .catch(err => {
          ElMessage({
            type: 'warning',
            message:
              err.response?.data?.message ?? 'Что-то не так с одобрением видео',
          })
        })
    }

    const allowEntry = item => {
      const entry = item
      ElMessageBox.confirm(
        `Вы действительно хотите одобрить это видео: <strong>${entry.name}</strong>?`,
        'Внимание',
        {
          type: 'success',
          cancelButtonText: 'Отмена',
          confirmButtonText: 'Одобрить',
          dangerouslyUseHTMLString: true,
          beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
              const resultOfAllowEntry = await requestToAllowEntry(entry)
              instance.confirmButtonLoading = true
              instance.confirmButtonText = 'В процессе...'
              if (resultOfAllowEntry) {
                requestItems()
                instance.confirmButtonLoading = false
                ElMessage({
                  type: 'success',
                  message: 'Одобрение видео подтверждено',
                })
                done()
              }
            } else {
              ElMessage({
                type: 'warning',
                message: 'Одобрение видео отменено',
              })
              done()
            }
          },
        }
      )
    }

    const requestToRejectEntry = item => {
      const params = {
        reasons: item.reasons.map(item => item.value.id),
      }

      return ApiService.post(`/admin/videos/${item.id}/reject`, params)
        .then(response => {
          return response.data.result
        })
        .catch(err => {
          ElMessage({
            type: 'warning',
            message:
              err.response?.data?.message ??
              'Что-то не так с отклонением видео',
          })
        })
    }

    const rejectEntry = item => {
      const entry = item
      ElMessageBox.confirm(
        `Вы действительно хотите отклонить это видео: <strong>${entry.name}</strong>?`,
        'Внимание',
        {
          type: 'success',
          cancelButtonText: 'Отмена',
          confirmButtonText: 'Отклонить',
          dangerouslyUseHTMLString: true,
          beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
              const resultOfAllowEntry = await requestToRejectEntry(entry)
              instance.confirmButtonLoading = true
              instance.confirmButtonText = 'В процессе...'
              if (resultOfAllowEntry) {
                requestItems()
                instance.confirmButtonLoading = false
                ElMessage({
                  type: 'success',
                  message: 'Отклонение видео подтверждено',
                })
                done()
              }
            } else {
              ElMessage({
                type: 'warning',
                message: 'Отклонение видео отменено',
              })
              done()
            }
          },
        }
      )
    }

    onMounted(() => {
      requestReasons()
    })

    return {
      rowClasses,
      isLoading,
      itemsList,
      videosFilterData,
      totalPages,
      totalItems,
      reasons,
      makeStatusTitle,
      lightboxVisible,
      lightboxImage,
      openLightbox,
      closeLightbox,
      allowEntry,
      rejectEntry,
      allowEntriesBulk,
      rejectEntriesBulk,
      deleteEntriesBulk,
      bulkRejectStatus,
      rejectButtonTitle,
      checkedModel,
      switchAllCheckboxes,
      convertDataForEdit,
      checkItemOnReasonSelect,
      filterVideosButtonClick,
      tableHasContent,
      someCheckboxesAreChecked,
      isEmptyObject,
    }
  },
})
